import { useCallback, useRef, Fragment } from 'react';

import { uniqBy } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import Preview from 'components/Pages/Admin/shared/Preview';
import Modal from 'components/shared/Modal';
import { Select } from 'components/shared/Select/adapters/FinalForm';
import { SafariContentOption } from 'components/shared/Select/options/SafariContent';
import { PAGE_STATUS_APPROVED } from 'constants/index';
import { useSnackbar } from 'hooks/useSnackbar';
import { closeModalAction, openModalAction } from 'store/modal';
import { isModalOpenedSelector, modalDataSelector } from 'store/modal/selectors';
import { setAppHasChangesAction } from 'store/unsavedChanges';
import { appHasChangesSelector } from 'store/unsavedChanges/selectors';
import { accountIdSelector, isAgentSelector } from 'store/user/selectors';
import { axiosGetAutoCancelDecorator } from 'utils/axiosGetAutoCancelDecorator';
import { createOptionFromPage } from 'utils/createOptionFromPage';
import { decamelizeKeys } from 'utils/humps';

import { openContentCreationModal } from '../../Details/DetailsChronologicalMode/Event/ModalContents/Edit/shared/ContentCreationModal';

const decoratedAxiosGet = axiosGetAutoCancelDecorator();
function SelectPage({
  name,
  pageType,
  extraLoadOptionsParams = {},
  placeholder,
  additionalQueryParams = {},
  additionalOptions = [],
  creatable = true,
  ...restProps
}) {
  const isAgent = useSelector(isAgentSelector);
  const dispatch = useDispatch();
  const accountId = useSelector(accountIdSelector);
  const selectRef = useRef();
  const onSnackbar = useSnackbar();
  const modalPreviewNameSpace = name ? `${name}_PAGE_PREVIEW` : 'PAGE_PREVIEW';
  const openModal = (pageId) => {
    dispatch(openModalAction({ type: modalPreviewNameSpace, data: { pageId } }));
  };
  const closeModal = () => {
    dispatch(closeModalAction());
  };
  const isPreviewModalOpen = useSelector(isModalOpenedSelector(modalPreviewNameSpace));
  const modalData = useSelector(modalDataSelector);
  const appHasChanges = useSelector(appHasChangesSelector);

  const handleCreatePage = async (pageName) => {
    const initAppHasChanges = appHasChanges;
    // setAppChanges required because the page and the modal watch for one store
    dispatch(setAppHasChangesAction(false));

    try {
      const res = await openContentCreationModal({
        type: 'area',
        initialValues: { attachedPage: { name: pageName } },
        withoutOneOff: true,
      });
      dispatch(setAppHasChangesAction(initAppHasChanges));
      if (!res) return;

      onSnackbar(`Page '${res.name}' has been created.`);
      createOptionFromPage(res);
      // call onChange required because select is unfocuse, lockMenu not working
      restProps.onChange(res);
      dispatch(setAppHasChangesAction(true));
    } catch (error) {
      dispatch(setAppHasChangesAction(initAppHasChanges));
      const handleErrorMsg = error?.errors?.source?.handle?.[0];
      if (handleErrorMsg) {
        onSnackbar(handleErrorMsg, true);
      } else {
        onSnackbar("Page couldn't be saved. Please try again.", true);
      }
      throw error;
    }
  };

  const loadOptions = async (q) => {
    const response = await decoratedAxiosGet(`/acc/${accountId}/pages/dropdown`, {
      params: decamelizeKeys({
        q,
        ...additionalQueryParams,
        account_id: accountId,
        types: [pageType],
        limit: 100,
        statuses: [PAGE_STATUS_APPROVED],
        orderBy: 'handle',
        ...extraLoadOptionsParams,
      }),
    });

    return uniqBy(
      (opt) => opt.value,
      [
        ...response.data.data.map((page) => createOptionFromPage(page, { isAgent })),
        ...additionalOptions.map((page) => createOptionFromPage(page, { isAgent })),
      ],
    );
  };

  const OptionMemoized = useCallback(
    (props) => {
      return (
        <SafariContentOption
          hasPreview={props.data.optionActionAvailable}
          onPreview={() => openModal(props.data.value)}
          hasContentIndicator={props.data.showContentIndicator}
          shared={props.data?.shared}
          {...props}
        />
      );
    },
    [openModal],
  );

  return (
    <Fragment
      /**
       * Using `key` with the dynamic value `JSON.stringify(additionalQueryParams)` ensures that
       * whenever `additionalQueryParams` changes, the `Select` component re-renders,
       * triggering a new `loadOptions` request. This is necessary to refresh the data
       * based on the updated query parameters.
       */
      key={JSON.stringify(additionalQueryParams)}
    >
      <Select
        {...restProps}
        name={name}
        placeholder={placeholder || 'Please enter page name here'}
        async
        creatable={creatable}
        allowCreateWhileLoading
        isValidNewOption={(input) => !!input}
        loadOptions={loadOptions}
        selectRef={selectRef}
        lockMenu={isPreviewModalOpen}
        renderNoOptionsMessage={() => 'No pages found'}
        onCreateOption={handleCreatePage}
        components={{
          Option: OptionMemoized,
        }}
      />

      <Modal noPadding size="lg" isOpen={isPreviewModalOpen} toClose={closeModal}>
        {isPreviewModalOpen && <Preview pageId={modalData.pageId} />}
      </Modal>
    </Fragment>
  );
}

export default SelectPage;
